import styled from "@emotion/styled";
import { memo, type ButtonHTMLAttributes, type Ref } from "react";
import { pxToRem } from "../../../design/utils";
import React from "react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly ref?: Ref<HTMLButtonElement>;
}

export const UnsetButton = styled.button`
  all: unset;
  cursor: pointer;
  flex: 0 0 auto;
`;

export const Button = styled.button`
  ${({ theme }) => theme.customTypography.label._1};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
  flex-shrink: 0;
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colours.primary.mid.dark20};
  border: 0px;
  height: ${pxToRem(32)};
`;

export const PrimaryButton = styled(Button)`
  ${({ theme }) => theme.layers.relief.primaryDark_10.shadow};
  color: ${({ theme }) => theme.colours.primary.light._20};
  &:disabled {
    color: ${({ theme }) => theme.colours.primary.light._50};
  }
  &:not(:disabled),
  &:not([disabled]) {
    &:hover {
      ${({ theme }) => theme.layers.relief.primaryMidDark_20.shadowHigher};
      color: ${({ theme }) => theme.colours.primary.light._10};
    }
    &:active {
      ${({ theme }) => theme.layers.inset.primaryDark_10.shadow};
      color: ${({ theme }) => theme.colours.primary.light._30};
    }
  }
`;

export const SecondaryButton = styled(Button)`
  ${({ theme }) => theme.layers.relief.uiGrey_10.shadowHigher};
  color: ${({ theme }) => theme.colours.uIGrey.dark._20};
  &:not(:disabled),
  &:not([disabled]) {
    &:hover {
      ${({ theme }) => theme.layers.relief.uiGrey_10.shadow};
      color: ${({ theme }) => theme.colours.uIGrey.dark._10};
    }
    &:active {
      ${({ theme }) => theme.layers.relief.uiGrey_30.shadow};
      color: ${({ theme }) => theme.colours.uIGrey.mid.mid};
    }
  }
`;

export const TertiaryButton = styled(Button)`
  ${({ theme }) => theme.layers.relief.accent_10.shadowHigher};
  color: ${({ theme }) => theme.colours.accent.dark._20};
  &:not(:disabled),
  &:not([disabled]) {
    &:hover {
      ${({ theme }) => theme.layers.relief.accent_5.shadow};
      color: ${({ theme }) => theme.colours.accent.dark._10};
    }
    &:active {
      ${({ theme }) => theme.layers.relief.accent_50.noShadow};
      color: ${({ theme }) => theme.colours.accent.dark._60};
    }
  }
`;

const StyledIconButton = styled(Button)<{ active?: boolean }>`
  padding: 0;
  height: ${pxToRem(32)};
  width: ${pxToRem(32)};
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  ${({ theme, active }) =>
    active
      ? theme.layers.relief.accent_50.noShadow
      : theme.layers.relief.accent_10.shadowHigher};
`;

interface IconButtonProps extends ButtonProps {
  src: string;
  alt: string;
  active?: boolean;
  imgHeight?: string;
}

export const IconButton = memo(
  React.forwardRef(
    (
      {
        src,
        alt,
        onClick,
        active,
        imgHeight,
        title,
        className,
      }: IconButtonProps,
      ref: React.ForwardedRef<HTMLButtonElement>,
    ) => (
      <StyledIconButton
        ref={ref}
        onClick={onClick}
        active={active}
        className={className}
        title={title}
      >
        <img src={src} alt={alt} height={imgHeight} title={title} />
      </StyledIconButton>
    ),
  ),
);
